import { IPointsMallCouponDetail, IPointsMallInfo } from '@/api/types/account';
import { IPrice, IProductDetail } from '@/api/types/public';
import { useStore } from '@/providers/app-store-provider';
import { colors, font } from '@/theme/zeelool.theme';
import { formatPriceNumberWithSymbol, genPrice } from '@/utils/price';
import { Box } from '@mui/material';
import { useTranslations } from '@/i18n/use-translations';

interface ProductBasicInfoProps {
  detail?: IProductDetail & { point_price_info?: IPrice[] };
  glassesFilter: () => string;
  pointsMallInfo: IPointsMallInfo;
  couponDetail?: IPointsMallCouponDetail | any;
  isProductDetail: boolean;
}

export default function ProductBasicInfo({
  detail,
  glassesFilter,
  pointsMallInfo,
  couponDetail,
  isProductDetail,
}: ProductBasicInfoProps) {
  const t = useTranslations('account.pointsMallDetail.productBasicInfo');
  const currency = useStore((state) => state.currency);
  const getDisplayName = () => {
    if (isProductDetail) {
      if (detail?.short_name) {
        return `${detail.short_name}${glassesFilter()}`;
      }
      return '';
    }
    return <>{couponDetail?.model_type === 4 ? t('shippingCoupon') : t('coupon')}</>;
  };

  const getPointsDisplay = () => {
    const points = isProductDetail ? detail.point : couponDetail.point;
    return `${points} ${points > 1 ? t('points') : t('point')}`;
  };

  const getPriceDisplay = () => {
    if (!isProductDetail) return null;
    if (Number(genPrice(detail.point_price_info, currency.name).sell_price) > 0) {
      return (
        <span className="notranslate">
          &nbsp;+
          {formatPriceNumberWithSymbol(
            genPrice(detail.point_price_info, currency.name).sell_price,
            currency,
          )}
        </span>
      );
    }
  };

  return (
    <Box sx={{ padding: '16px 14px' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: 2,
          fontSize: '14px',
          lineHeight: '17px',
        }}
      >
        <Box sx={{ fontFamily: font.bb }}>{getDisplayName()}</Box>
        {isProductDetail && detail.product_frame_color_name && (
          <Box>{detail.product_frame_color_name}</Box>
        )}
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          marginBottom: 1,
        }}
      >
        <Box
          sx={{
            fontSize: '20px',
            lineHeight: '24px',
            fontFamily: font.bb,
          }}
        >
          <span className="text-warning">{getPointsDisplay()}</span>
          {getPriceDisplay()}
        </Box>
      </Box>
      <div className="mb-20 text-14">{t('yourPoints', { points: pointsMallInfo?.points })}</div>
      {isProductDetail && (
        <Box
          sx={{
            fontSize: '14px',
            color: colors.auxiliaryText,
          }}
        >
          {t('sku', { code: detail.shop_sku })}
        </Box>
      )}
    </Box>
  );
}
